import { useQueryClient } from '@tanstack/react-query';
import { hydraUiApi, useHydraUIAPIMutation, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import type { BillingAutoTopUpSettings } from '@customerio/hydra-ui-api';
import { sleep } from '@/utils/sleep';
import { DEFAULT_AUTO_TOP_UP_SETTINGS, type AutoTopUpSettings } from '../utils/add-funds';

export type AutoTopUp =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | {
      status: 'ready';
      hasCard: boolean;
      /** False until the account first saves a rule. `settings` then holds the defaults. */
      hasSavedRule: boolean;
      settings: AutoTopUpSettings;
      disabledReason: string | null;
    };

export const AUTO_TOP_UP_QUERY_KEY = ['getWalletAutoTopUp'];

// The backend accepts auto reload only once Stripe's customer.updated webhook has stored the card, which
// can land seconds after a new card is saved. About 10 seconds of checks covers it.
const CARD_CHECK_ATTEMPTS = 5;
const CARD_CHECK_DELAY_MS = 2000;

function autoTopUpQueryKey(accountId: number) {
  return [...AUTO_TOP_UP_QUERY_KEY, { params: { account_id: accountId } }];
}

function fetchAutoTopUp(accountId: number, signal?: AbortSignal) {
  return hydraUiApi.billing.getWalletAutoTopUp({ params: { path: { account_id: String(accountId) } }, signal });
}

export function useAutoTopUp(): AutoTopUp {
  const { account } = useAuth();
  const isFlex = isFlexPlan(account);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: autoTopUpQueryKey(account.id),
    queryFn: ({ signal }) => fetchAutoTopUp(account.id, signal),
    queryConfig: { enabled: isFlex },
  });

  if (!isFlex || isError) return { status: 'unavailable' };
  if (isLoading || data == null) return { status: 'loading' };

  return {
    status: 'ready',
    hasCard: data.has_card,
    hasSavedRule: data.settings != null,
    settings: savedSettings(data.settings),
    disabledReason: data.settings?.disabled_reason ?? null,
  };
}

function savedSettings(settings: BillingAutoTopUpSettings | undefined): AutoTopUpSettings {
  if (settings == null) return DEFAULT_AUTO_TOP_UP_SETTINGS;
  return {
    enabled: settings.enabled,
    threshold_usd: settings.threshold_usd,
    target_usd: settings.target_usd,
    period_cap_enabled: settings.period_cap_enabled,
    // Keeps an amount in the field for when the limit is turned back on.
    period_cap_usd: settings.period_cap_usd ?? DEFAULT_AUTO_TOP_UP_SETTINGS.period_cap_usd,
  };
}

/** The rule set up starts from: the saved one, so its cap is kept, or the defaults with no cap. */
export function setupSettings(saved: AutoTopUp): AutoTopUpSettings {
  if (saved.status === 'ready' && saved.hasSavedRule) return saved.settings;
  return { ...DEFAULT_AUTO_TOP_UP_SETTINGS, period_cap_enabled: false };
}

export function useSaveAutoTopUp() {
  const { account } = useAuth();
  const queryClient = useQueryClient();

  return useHydraUIAPIMutation(
    (settings: AutoTopUpSettings) =>
      hydraUiApi.billing.updateWalletAutoTopUp({
        params: { path: { account_id: String(account.id) } },
        body: settings,
      }),
    {
      // The save answers with the settings as the GET reports them.
      onSuccess: (response) => {
        queryClient.setQueryData(autoTopUpQueryKey(account.id), response);
      },
    },
  );
}

/** Resolves true once the backend reports a card it can charge, or false when it does not in time. */
export function useWaitForAutoTopUpCard() {
  const { account } = useAuth();
  const queryClient = useQueryClient();

  return async (): Promise<boolean> => {
    for (let attempt = 0; attempt < CARD_CHECK_ATTEMPTS; attempt++) {
      if (attempt > 0) await sleep(CARD_CHECK_DELAY_MS);
      const state = await queryClient.fetchQuery({
        queryKey: autoTopUpQueryKey(account.id),
        queryFn: ({ signal }) => fetchAutoTopUp(account.id, signal),
        staleTime: 0,
      });
      if (state.has_card) return true;
    }
    return false;
  };
}
